export function normaliseScenePathForComparison(scenePath: string | null | undefined): string | null {
  if (!scenePath) {
    return null;
  }
  return scenePath.replace(/\\/g, '/').toLowerCase();
}

export function areScenePathsEquivalent(
  leftPath: string | null | undefined,
  rightPath: string | null | undefined
): boolean {
  const normalisedLeftPath = normaliseScenePathForComparison(leftPath);
  const normalisedRightPath = normaliseScenePathForComparison(rightPath);
  if (!normalisedLeftPath || !normalisedRightPath) {
    return false;
  }
  return normalisedLeftPath === normalisedRightPath;
}

export interface SceneUpdatePlanLike {
  updateOperations: Array<{ filePath: string }>;
}

export interface ResolveSceneReloadPathOptions {
  currentScenePath: string | null;
  oldPath: string;
  newPath: string;
  updatePlan: SceneUpdatePlanLike;
}

export function resolveSceneReloadPathAfterRename({
  currentScenePath,
  oldPath,
  newPath,
  updatePlan,
}: ResolveSceneReloadPathOptions): string | null {
  if (areScenePathsEquivalent(currentScenePath, oldPath)) {
    return newPath;
  }
  const sceneWillBeUpdated = currentScenePath && updatePlan.updateOperations.some(
    operation => areScenePathsEquivalent(operation.filePath, currentScenePath)
  );
  return sceneWillBeUpdated ? currentScenePath : null;
}

export interface ResolveDefaultSceneSettingsUpdateOptions {
  oldPath: string;
  defaultScene: string | null | undefined;
  defaultServerScene: string | null | undefined;
}

export function resolveDefaultSceneSettingsUpdate({
  oldPath,
  defaultScene,
  defaultServerScene,
}: ResolveDefaultSceneSettingsUpdateOptions): { updateGameDefault: boolean, updateServerDefault: boolean } {
  return {
    updateGameDefault: areScenePathsEquivalent(defaultScene, oldPath),
    updateServerDefault: areScenePathsEquivalent(defaultServerScene, oldPath),
  };
}

export interface SceneScreenshotMigrationKeys {
  oldScopedKey: string | null;
  newScopedKey: string | null;
  oldLegacyKey: string | null;
  newLegacyKey: string | null;
}

export interface SceneScreenshotMigrationTarget {
  sourceKey: string;
  targetKey: string;
}

export function resolveSceneScreenshotMigrationTargets(keys: SceneScreenshotMigrationKeys): SceneScreenshotMigrationTarget[] {
  const candidates: Array<SceneScreenshotMigrationTarget | null> = [
    keys.oldScopedKey && keys.newScopedKey
      ? { sourceKey: keys.oldScopedKey, targetKey: keys.newScopedKey }
      : null,
    keys.oldLegacyKey && keys.newLegacyKey
      ? { sourceKey: keys.oldLegacyKey, targetKey: keys.newLegacyKey }
      : null,
  ];

  const dedupedTargets = new Map<string, SceneScreenshotMigrationTarget>();
  for (const candidate of candidates) {
    if (!candidate || candidate.sourceKey === candidate.targetKey) {
      continue;
    }
    dedupedTargets.set(`${candidate.sourceKey}=>${candidate.targetKey}`, candidate);
  }
  return Array.from(dedupedTargets.values());
}

export function shouldShowCurrentSceneThumbnailRefreshAction(
  isSceneAsset: boolean,
  assetPath: string | null | undefined,
  currentScenePath: string | null | undefined
): boolean {
  if (!isSceneAsset || !assetPath) {
    return false;
  }
  return areScenePathsEquivalent(assetPath, currentScenePath);
}
